import { useMemo } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AnomalyRow {
    idEmployee: string;
    Nama: string;
    Jabatan: string | null;
    schedule_type: string;
    shift_date: string;
    window_start: string;
    window_end: string;
    heart_rate_count: number;
    heart_rate_min: number | null;
    heart_rate_max: number | null;
    heart_rate_avg: number | null;
    sleep_motion_count: number;
    heart_rate_anomaly: boolean;
    sleep_anomaly: boolean;
    has_anomaly: boolean;
    device_name?: string;
    device_id?: string;
}

interface AnomalyModalProps {
    isOpen: boolean;
    onClose: () => void;
    rows: AnomalyRow[];
    anomalyCount: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AnomalyModal({
    isOpen,
    onClose,
    rows,
    anomalyCount,
}: AnomalyModalProps) {
    // Only show rows with actual anomalies
    const anomalyRows = useMemo(() => {
        return rows.filter(r => r.has_anomaly);
    }, [rows]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="relative bg-white rounded-lg shadow-xl w-full h-full max-w-full max-h-full flex flex-col">
                    {/* Header */}
                    <div className="bg-orange-600 px-6 py-4 flex items-center justify-between rounded-t-lg">
                        <div className="flex items-center gap-3">
                            <h2 className="text-2xl font-bold text-white">Data Anomaly</h2>
                            <span className="bg-white bg-opacity-20 text-dark px-3 py-1 rounded-full text-sm font-semibold">
                                {anomalyCount} employees
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:bg-gray-500/50 rounded-full p-2 transition-colors"
                            title="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-auto p-6">
                        {anomalyRows.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center">
                                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-gray-500 text-lg">No anomalies detected — all data looks normal</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max">
                                    <thead>
                                        <tr className="border-b-2 border-gray-300 bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Jabatan</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Device</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Shift</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Shift Date</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">HR Count</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">HR Min</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">HR Max</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">HR Avg</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">HR Anomaly</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Sleep Count</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Sleep Anomaly</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {anomalyRows.map((row, index) => (
                                            <tr
                                                key={`${row.idEmployee}-${row.shift_date}-${index}`}
                                                className={`border-b border-gray-200 hover:bg-orange-50 transition-colors ${
                                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                }`}
                                            >
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{row.Nama}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.Jabatan || '-'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.device_name || row.device_id || '-'}</td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                                                    <span className={`inline-block px-2 py-1 text-xs rounded font-medium ${
                                                        row.schedule_type === 'day' ? 'bg-yellow-100 text-yellow-800' : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                        {row.schedule_type === 'day' ? 'Day Shift' : 'Night Shift'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                                                    {row.shift_date}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap font-semibold">
                                                    {row.heart_rate_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap text-gray-600">
                                                    {row.heart_rate_min !== null ? row.heart_rate_min : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap text-gray-600">
                                                    {row.heart_rate_max !== null ? row.heart_rate_max : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap text-gray-600">
                                                    {row.heart_rate_avg !== null ? row.heart_rate_avg : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                                                    <AnomalyBadge isAnomaly={row.heart_rate_anomaly} />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap font-semibold">
                                                    {row.sleep_motion_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                                                    <AnomalyBadge isAnomaly={row.sleep_anomaly} />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    <span className="inline-block px-2 py-1 text-xs rounded font-medium bg-slate-100 text-slate-800">
                                                        {row.sleep_anomaly && (
                                                            <span>Indikasi Smartwatch Tidak Digunakan Pada Saat Tidur</span>
                                                        )}
                                                        {!row.sleep_anomaly && row.heart_rate_anomaly && (
                                                            <span>Indikasi Pemakaian Smartwatch Longgar / Posisi Salah</span>
                                                        )}
                                                        {!row.sleep_anomaly && !row.heart_rate_anomaly && (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg">
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-600">
                                Showing {anomalyRows.length} employee{anomalyRows.length !== 1 ? 's' : ''} with data anomalies
                            </p>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Anomaly Badge Sub-component ─────────────────────────────────────────────

function AnomalyBadge({ isAnomaly }: { isAnomaly: boolean }) {
    if (isAnomaly) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                Anomaly
            </span>
        );
    }

    return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            OK
        </span>
    );
}
